import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective } from '@jsverse/transloco';
import type { InsuranceRedirectResponse, InsuranceVendor, StateLicenseVerbiage } from '@ld.consumer-portal/models';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';
import { LoggingService } from '@ld.consumer-portal/utils';

export interface InsuranceTransitionEvent {
  vendor: InsuranceVendor;
  redirectUrl: string;
}

/**
 * Insurance Transition Modal - Shows transition screen before redirecting to vendor.
 * Displays vendor logo, disclosure text, and state license verbiage.
 *
 * Based on legacy: Web/LD.CP.Web/angular/src/app/modals/geico-transition.modal.ts
 */
@Component({
  selector: 'ld-insurance-transition-modal',
  imports: [BaseModalComponent, MatIconModule, MatProgressSpinnerModule, TranslocoDirective],
  templateUrl: './insurance-transition-modal.component.html',
  styleUrl: './insurance-transition-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsuranceTransitionModalComponent implements OnInit {
  private readonly loggingService = inject(LoggingService);

  readonly loanId = input.required<string>();

  readonly borrowerId = input.required<string>();

  readonly vendor = input.required<InsuranceVendor>();

  readonly getStateLicenseVerbiageFn = input.required<
    (loanId: string, borrowerId: string) => Promise<StateLicenseVerbiage>
  >();

  readonly getInsuranceRedirectUrlFn = input.required<
    (loanId: string, borrowerId: string) => Promise<InsuranceRedirectResponse>
  >();

  readonly getMelloInsurancePortalUrlFn = input.required<() => Promise<InsuranceRedirectResponse>>();

  readonly continue = output<InsuranceTransitionEvent>();

  readonly cancel = output<void>();

  protected readonly isLoading = signal(true);

  protected readonly isProcessing = signal(false);

  protected readonly verbiage = signal<StateLicenseVerbiage | null>(null);

  protected readonly redirectUrl = signal<string | null>(null);

  protected readonly errorMessage = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    if (this.vendor() === 'geico') {
      await this.loadVerbiage();
    } else {
      this.isLoading.set(false);
    }
  }

  private async loadVerbiage(): Promise<void> {
    try {
      const verbiage = await this.getStateLicenseVerbiageFn()(this.loanId(), this.borrowerId());
      this.verbiage.set(verbiage);
    } catch (err) {
      this.loggingService.logException(err, '[InsuranceTransition] Error loading verbiage:');
      // Non-critical - continue without verbiage
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async onContinue(): Promise<void> {
    this.isProcessing.set(true);
    this.errorMessage.set(null);

    const newWindow = window.open('about:blank', '_blank');

    try {
      const response =
        this.vendor() === 'geico'
          ? await this.getInsuranceRedirectUrlFn()(this.loanId(), this.borrowerId())
          : await this.getMelloInsurancePortalUrlFn()();

      this.redirectUrl.set(response.redirectUrl);

      this.continue.emit({
        vendor: this.vendor(),
        redirectUrl: response.redirectUrl,
      });

      if (newWindow && response.redirectUrl) {
        newWindow.location.href = response.redirectUrl;
      }
    } catch (err) {
      newWindow?.close();
      const message = err instanceof Error ? err.message : 'Failed to get insurance quote URL';
      this.errorMessage.set(message);
    } finally {
      this.isProcessing.set(false);
    }
  }

  protected onCancel(): void {
    this.cancel.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isProcessing()) {
      this.cancel.emit();
    }
  }

  protected getVendorName(): string {
    return this.vendor() === 'geico' ? 'GEICO' : 'melloInsurance';
  }

  protected getVendorDescription(): string {
    if (this.vendor() === 'geico') {
      return 'Redirecting to GEICO for your homeowners insurance quote';
    }
    return "Let's get you to melloInsurance";
  }

  protected getAuthorizationText(): string {
    if (this.vendor() === 'geico') {
      return 'By selecting Agree and Continue you are authorizing loanDepot.com to share your personal information with GEICO in order to obtain insurance information and quotes relating to your circumstances.';
    }
    return 'By clicking Continue you are authorizing loanDepot.com to share your personal information with melloInsurance Services (MIS) to assist MIS in obtaining insurance information and quotes relating to your circumstances.';
  }

  protected getContinueButtonText(): string {
    if (this.vendor() === 'geico') {
      return 'Agree and Continue';
    }
    return 'Continue';
  }

  protected getPrivacyPolicyUrl(): string {
    if (this.vendor() === 'geico') {
      return 'https://www.loandepot.com/privacypolicy';
    }
    return 'https://www.melloinsurance.com/privacy-policy';
  }

  protected getPrivacyPolicyLabel(): string {
    if (this.vendor() === 'geico') {
      return "loanDepot's Privacy Policy";
    }
    return 'MIS Privacy Policy';
  }
}
